/** @jsxRuntime classic */
/** @jsx jsx */
import {jsx} from '@emotion/react';
import {FunctionComponent, useEffect, useState} from 'react';
import {renderItemLink, renderLocationLink} from '../../../libs/helpers';
import {CycleCountItem, REASON_CODES, REASON_CODES_BY_NAME, REASON_CODES_BY_VALUE} from './CycleCountInterfaces';
import {calculateDeltaForCountedItem, CycleCountReasonCode} from './helpers';

interface CompleteCycleCountDiscrepancyStepProps {
  cycleCountReservationId: number;
  cycleCountItems: CycleCountItem[];
  enableReservationSelector: boolean;
  lineLevelReasonCaptureEnabled: boolean;
  onReasonCodeChange: (reasonCode: string, ccItemId?: string) => void;
  onRequiredActionsComplete: (disable: boolean) => void;
}

const DISCREPANCY_WARNING_THRESHOLD = 0.2;

const CompleteCycleCountDiscrepancyStep: FunctionComponent<CompleteCycleCountDiscrepancyStepProps> = ({
  cycleCountReservationId,
  cycleCountItems,
  enableReservationSelector,
  lineLevelReasonCaptureEnabled,
  onReasonCodeChange,
  onRequiredActionsComplete
}) => {
  const [masterReasonCode, setMasterReasonCode] = useState<CycleCountReasonCode>(null);
  const [linesWithDiscrepancies, setLinesWithDiscrepancies] = useState<CycleCountItem[]>([]);
  const headers = ['SKU', 'Location', 'Expected', 'Counted', 'Difference'];

  useEffect(() => {
    syncItems(cycleCountItems);
  }, [cycleCountItems]);

  useEffect(() => {
    checkRequiredActions();
  }, [linesWithDiscrepancies]);

  const syncItems = (items: CycleCountItem[]) => {
    const relevantItems = items.filter((item) => !item.lpnBarcode);
    setLinesWithDiscrepancies(relevantItems);
  };

  const checkRequiredActions = () => {
    const itemsWithSignificantDiscrepancies = linesWithDiscrepancies.filter((item) => {
      const [, , totalExpectedEaches, delta] = calculateDeltaForCountedItem(item);
      return Math.abs(delta / totalExpectedEaches) >= DISCREPANCY_WARNING_THRESHOLD;
    });
    const requiredReasonCodesDefined =
      itemsWithSignificantDiscrepancies.length === 0 ||
      itemsWithSignificantDiscrepancies.every((item) => item.reasonCode != null);

    onRequiredActionsComplete(requiredReasonCodesDefined);
  };

  const setRowColor = (totalExpectedEaches: number, delta: number, hasDiffResId: boolean) => {
    // Rows with wrong reservations are always unexpected, show check that first
    if (hasDiffResId && enableReservationSelector) {
      return 'bg-invalidLight';
    } else if (Math.abs(delta / totalExpectedEaches) >= DISCREPANCY_WARNING_THRESHOLD) {
      return 'bg-warningPeel';
    }
  };

  const renderSku = (item: CycleCountItem, isDiffReservation: boolean) => {
    return (
      <div>
        <div>{renderItemLink(item.inventory.id, item.inventory.sku, false)}</div>
        <div>{item.inventory.description}</div>
        {isDiffReservation && enableReservationSelector && (
          <div className="red7">{`*Wrong Reservation #${item?.reservationId}`}</div>
        )}
      </div>
    );
  };

  const handleItemReasonChange = (ccItemId: string, reasonCodeName: string) => {
    const reason = REASON_CODES_BY_NAME[reasonCodeName];
    const reasonChanged = linesWithDiscrepancies.find((item) => item.id === ccItemId).reasonCode !== reason?.value;

    if (reasonChanged) {
      onReasonCodeChange(reason.value, ccItemId);
    }
  };

  const handleMasterReasonChange = (reasonCodeName: string) => {
    const reason = REASON_CODES_BY_NAME[reasonCodeName];
    const reasonChanged = linesWithDiscrepancies.some((item) => item.reasonCode !== reason?.value);

    if (reasonChanged) {
      setMasterReasonCode(reason);
      onReasonCodeChange(reason.value, null);
    }
  };

  const renderMasterReasonCode = () => {
    return (
      <div id={'master-reason-code'}>
        <span className="reason-icon">
          {masterReasonCode ? (
            <i className="fa fa-pencil" aria-hidden="true"></i>
          ) : (
            <i className="fa fa-plus" aria-hidden="true"></i>
          )}
        </span>
        <select
          id="masterReasonCode"
          data-testid="masterReasonCode"
          value={masterReasonCode?.name}
          onChange={(e) => handleMasterReasonChange(e.target.value)}
        >
          <option value="">Reason For All</option>
          {REASON_CODES.filter((reason) => reason.value !== null).map((reason) => (
            <option key={reason.value} value={reason.name}>
              {reason.name}
            </option>
          ))}
        </select>
      </div>
    );
  };

  const renderReasonCodes = (item: CycleCountItem) => {
    return (
      <div className={'reason-code'}>
        <span className="reason-icon">
          {item.reasonCode ? (
            <i className="fa fa-pencil" aria-hidden="true"></i>
          ) : (
            <i className="fa fa-plus" aria-hidden="true"></i>
          )}
        </span>
        <select
          data-testid={`reasonSelect${item.id}`}
          value={REASON_CODES_BY_VALUE[item.reasonCode]?.name}
          onChange={(e) => handleItemReasonChange(item.id, e.target.value)}
          onClick={(e) => e.stopPropagation()} // prevent event triggers to parents
        >
          <option value="">Reason</option>
          {REASON_CODES.filter((reason) => reason.value !== null).map((reason) => (
            <option key={reason.value} value={reason.name}>
              {reason.name}
            </option>
          ))}
        </select>
      </div>
    );
  };

  return (
    <div id={'cc-discrepancy-step'}>
      <p>This Count has discrepancies. Before completing it, you can apply a reason to every line with an issue.</p>
      {lineLevelReasonCaptureEnabled && renderMasterReasonCode()}
      <table className="table table-striped sticky">
        <thead>
          <tr>
            {headers.map((header, index) => {
              return <th key={index}>{header}</th>;
            })}
          </tr>
        </thead>
        <tbody>
          {linesWithDiscrepancies.map((item, index) => {
            const hasDifferentResId = item?.reservationId !== cycleCountReservationId;
            const [showDelta, totalCountedEaches, totalExpectedEaches, delta] = calculateDeltaForCountedItem(item);
            if ((showDelta && Math.abs(delta) > 0) || hasDifferentResId) {
              return (
                <tr key={index} className={setRowColor(totalExpectedEaches, delta, hasDifferentResId)}>
                  <td>{renderSku(item, hasDifferentResId)}</td>
                  <td>
                    <div style={{whiteSpace: 'nowrap'}}>
                      {renderLocationLink(item.location.id, item.location.label)}
                    </div>
                  </td>
                  <td>{totalExpectedEaches} ea</td>
                  <td>{totalCountedEaches} ea</td>
                  <td>
                    <b className="red7">{delta > 0 ? `+${delta}` : delta}</b>
                  </td>
                  {lineLevelReasonCaptureEnabled && (
                    <td>
                      {renderReasonCodes(item)}
                      {Math.abs(delta / totalExpectedEaches) >= DISCREPANCY_WARNING_THRESHOLD && !item.reasonCode && (
                        <div className="red8">*Over 20% Difference Required</div>
                      )}
                    </td>
                  )}
                </tr>
              );
            }
          })}
        </tbody>
      </table>
    </div>
  );
};

export default CompleteCycleCountDiscrepancyStep;
